/**
 * Koju turu otvoriti vozaču koji danas ima više od jedne.
 *
 * Izdvojeno iz ekrana da bi se moglo TESTIRATI: pravilo se već jednom lomilo
 * na terenu, i to tiho — vozač otvori link, vidi turu, i nema po čemu da zna
 * da nije ta.
 *
 * Istorija, jer objašnjava oblik:
 *
 * - Do 26.08.2026. se uvek uzimala prva tura dana, pa je vozač sa dve otvarao
 *   pogrešnu. Rešeno tako što poruka nosi i broj ture (`&tura=`).
 * - Do 03.09.2026. je to važilo samo za poruku. QR kod sa operaterovog ekrana
 *   nosi goli ključ, pa je Đurović posle završene prve ture skenirao QR i
 *   dobio — prvu turu, završenu, uz savet da otvori link koji je dobio uz
 *   drugu. Tog linka operater nema nigde na ekranu.
 *
 * - Do 08.09.2026. je vozač sa DVE nezavršene ture dobijao prvu i savet da za
 *   drugu otvori link koji je stigao uz nju — a taj link operater nema nigde na
 *   ekranu. Savet je bio neupotrebljiv, pa se sad pita.
 *
 * Otud pravilo: <b>tura iz linka ima prednost; bez nje se uzima jedina
 * nezavršena; a kad ih je više, vozač bira.</b> Završena tura je odrađen posao;
 * vozač koji otvara ekran traži ono što tek treba da uradi.
 */

/**
 * Je li datum ture danasnji dan.
 *
 * Od 28.09.2026. vozac dobija i ture od ranije koje nisu zavrsene — postoje da
 * bi se mogle zatvoriti. Takva tura mora da NOSI svoj datum na ekranu, inace
 * izgleda kao danasnji posao.
 *
 * Poredi se po danu u lokalnoj zoni, ne po tekstu: datum sa servera je
 * „2026-09-26", a telefon ume da bude u drugoj zoni.
 */
export function jeDanasnja(datum: string): boolean {
  const d = new Date(datum);

  if (Number.isNaN(d.getTime())) {
    // Nerazumljiv datum se NE prikazuje kao „od ...", jer bi to bila izmisljotina.
    return true;
  }

  const danas = new Date();

  return d.getFullYear() === danas.getFullYear()
    && d.getMonth() === danas.getMonth()
    && d.getDate() === danas.getDate();
}

/** Tura koliko izbor treba da zna o njoj. */
export interface TuraZaIzbor {
  id: number;
  name?: string;
  status?: string;
}

/** Status posle kog tura više nije posao. */
export function jeZavrsena(status?: string): boolean {
  return status === 'Completed' || status === 'Abandoned';
}

export interface IzborTure<T> {
  /** Tura koju treba otvoriti. Prazno kad se ne sme pogađati umesto vozača. */
  tura: T | null;
  /** Šta reći vozaču. Prazno kad nema šta — ćutanje je ovde tačan odgovor. */
  poruka: string | null;
  /**
   * Ture između kojih vozač bira sam.
   *
   * Prazno u svakom uobičajenom slučaju. Popunjava se samo kad danas ima više
   * od jedne NEZAVRŠENE ture, a link ne kaže koja je — tada nijedan odgovor
   * nije tačan i ekran mora da pita.
   */
  izbor: T[];
}

/**
 * @param tureIzLinka Broj ture iz `&tura=` u linku; prazno kad ga link nema.
 */
export function izaberiTuru<T extends TuraZaIzbor>(
  spisak: T[], tureIzLinka: number | null
): IzborTure<T> {
  if (spisak.length === 0) {
    return { tura: null, poruka: 'Za danas nema ture na tebe.', izbor: [] };
  }

  // Link je izričit: vozač je otvorio baš ovu turu i ne pogađa se umesto njega.
  const izLinka = tureIzLinka === null
    ? undefined
    : spisak.find((t) => t.id === tureIzLinka);

  if (izLinka) {
    return { tura: izLinka, poruka: null, izbor: [] };
  }

  const nezavrsene = spisak.filter((t) => !jeZavrsena(t.status));

  if (nezavrsene.length === 0) {
    // Sve je odrađeno. Otvara se poslednja, da vozač ima šta da pogleda, ali
    // mu se kaže da posla nema — inače misli da mu se ekran zaglavio.
    return {
      tura: spisak[spisak.length - 1],
      poruka: 'Sve današnje ture su završene.',
      izbor: []
    };
  }

  if (nezavrsene.length === 1) {
    // Jedina nezavršena — nema se šta birati, pa se i ne dosađuje porukom.
    return { tura: nezavrsene[0], poruka: null, izbor: [] };
  }

  // Dve nezavršene, a link ne kaže koja. Ovde nijedan odgovor nije tačan:
  // otvoriti prvu znači pogoditi, a do 08.09.2026. se vozaču govorilo da
  // otvori „link koji je stigao uz drugu" — link koji operater nema nigde na
  // ekranu, pa je savet bio neupotrebljiv. Zato se pita.
  return {
    tura: null,
    poruka: 'Danas imaš dve ture. Koju otvaraš?',
    izbor: nezavrsene
  };
}
